/**
 * Pure helpers for the Admin All Orders hub.
 * Extracted so they can be unit-tested without spinning up React /
 * jsdom against the huge FulfillmentAdmin component tree.
 */

// URL-safe filter serialisation. NEVER includes `q` (search text).
export function readUrlFilters(searchParams) {
  const list = (k) => (searchParams.get(k) || "").split(",").map((v) => v.trim()).filter(Boolean);
  const view = searchParams.get("view");
  return {
    preset: searchParams.get("preset") || "last_30",
    date_basis: searchParams.get("date_basis") || "created",
    date_from: searchParams.get("date_from") || null,
    date_to: searchParams.get("date_to") || null,
    status: list("status"),
    payment_method: list("payment_method"),
    fulfillment_group: list("fulfillment_group"),
    source: list("source"),
    source_type: list("source_type"),
    sort: searchParams.get("sort") || "newest",
    page: parseInt(searchParams.get("page") || "1", 10) || 1,
    view: (view === "open" || view === "stale" || view === "missing_information") ? view : null,
  };
}

export function toUrlParams(filters) {
  const p = {};
  const put = (k, v) => {
    if (v && (!Array.isArray(v) || v.length)) {
      p[k] = Array.isArray(v) ? v.join(",") : String(v);
    }
  };
  put("preset", filters.preset !== "last_30" ? filters.preset : null);
  put("date_basis", filters.date_basis !== "created" ? filters.date_basis : null);
  put("date_from", filters.date_from);
  put("date_to", filters.date_to);
  put("status", filters.status);
  put("payment_method", filters.payment_method);
  put("fulfillment_group", filters.fulfillment_group);
  put("source", filters.source);
  put("source_type", filters.source_type);
  put("sort", filters.sort !== "newest" ? filters.sort : null);
  put("page", filters.page > 1 ? filters.page : null);
  if (filters.view === "open" || filters.view === "stale" || filters.view === "missing_information") {
    p.view = filters.view;
  }
  return p;
}

// Pure PII scrubber — proves search text never leaks into URL params.
export function scrubForUrl(filters) {
  // Whitelist approach: only known-safe keys survive.
  const SAFE_KEYS = new Set([
    "preset", "date_basis", "date_from", "date_to",
    "status", "payment_method", "fulfillment_group",
    "source", "source_type", "sort", "page", "view",
  ]);
  const clean = {};
  for (const [k, v] of Object.entries(filters || {})) {
    if (SAFE_KEYS.has(k)) clean[k] = v;
  }
  return clean;
}

export const money = (cents) => {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
};

export const displayCustomer = (name) => {
  if (name === null || name === undefined || String(name).trim() === "") return "Unknown";
  return String(name);
};

export const displayPaymentMethod = (m) => {
  if (m === null || m === undefined || m === "" || m === "unknown") return "Unknown";
  const LABELS = {
    zelle: "Zelle", cash: "Cash", cashapp: "Cash App",
    card: "Credit/Debit Card", shopify_checkout: "Shopify Checkout",
    ebay: "eBay", other: "Other",
  };
  return LABELS[m] || m;
};

export function fulfillmentBadgeSpec(group) {
  const g = String(group || "").toLowerCase();
  if (g === "pickup") return { label: "PICKUP", tone: "emerald" };
  if (g === "ship")   return { label: "SHIPPING", tone: "blue" };
  return { label: "DELIVERY REVIEW NEEDED", tone: "red" };
}

// "Active filter" count used by the Filters button badge.
export function activeFilterCount(filters, debouncedSearch) {
  return (
    (filters.status?.length || 0) +
    (filters.payment_method?.length || 0) +
    (filters.fulfillment_group?.length || 0) +
    (filters.source?.length || 0) +
    (filters.source_type?.length || 0) +
    (filters.preset !== "last_30" ? 1 : 0) +
    (filters.date_basis !== "created" ? 1 : 0) +
    (debouncedSearch ? 1 : 0)
  );
}

// Quick-view chips — canonical presets applied via existing filters.
// The dashboard "All Orders" card click target — opens with a sane
// default (last_30 preset, zero active filters) so the page loads
// without a 20K-row pagination cap error and without a misleading
// "Filters · 1" badge. Users who want everything can switch preset
// to All Time via the dropdown or the "all" quick-view chip.
export const ALL_ORDERS_CARD_TARGET = "/admin/orders";

// Each chip clears incompatible filters and sets a small stable subset.
export const QUICK_VIEWS = [
  { key: "all",          label: "All Orders",           filters: { preset: "all_time" } },
  { key: "today",        label: "Today's Orders",       filters: { preset: "today" } },
  { key: "unpaid",       label: "Unpaid Orders",        filters: { preset: "all_time", status: ["awaiting_payment"] } },
  { key: "payment_rev",  label: "Payment Review",       filters: { preset: "all_time", status: ["payment_submitted", "payment_needs_review"] } },
  { key: "must_prepare", label: "Must Prepare",         filters: { preset: "all_time", status: ["payment_verified"] } },
  { key: "preparing",    label: "Preparing",            filters: { preset: "all_time", status: ["preparing"] } },
  { key: "pickup_ready", label: "Pickup Ready",         filters: { preset: "all_time", status: ["ready"], fulfillment_group: ["pickup"] } },
  { key: "ship_ready",   label: "Ship Ready",           filters: { preset: "all_time", status: ["ready"], fulfillment_group: ["ship"] } },
  { key: "review",       label: "Delivery Review Needed", filters: { preset: "all_time", status: ["ready"], fulfillment_group: ["unknown"] } },
  { key: "done_today",   label: "Completed Today",      filters: { preset: "today", date_basis: "completion", status: ["completed"] } },
  { key: "done_week",    label: "Completed This Week",  filters: { preset: "last_7", date_basis: "completion", status: ["completed"] } },
  { key: "closed",       label: "Cancelled / Refunded", filters: { preset: "all_time", status: ["cancelled", "refunded", "partially_refunded"] } },
];

// Apply a quick view — clears incompatible filters, keeps sort/page_size.
export function applyQuickView(currentFilters, view) {
  const cleared = {
    preset: "last_30", date_basis: "created", date_from: null, date_to: null,
    status: [], payment_method: [], fulfillment_group: [], source: [], source_type: [],
    sort: currentFilters?.sort || "newest", page: 1,
  };
  return { ...cleared, ...view.filters };
}
